import { Numbers } from '@buddy-technology/buddy_helpers';
import { POLICY_STATUSES } from '@buddy-technology/ion-helpers';
import {
	PAYMENT_TYPE, COVERED_TYPE, POLICY_TYPE,
} from '../models/SimpleModels';

const PolicyHelper = {
	/**
	 * Calculates the payment amount based on the premium total, payment option, and payments left.
	 *
	 * @param {number} premiumTotal - The total premium amount.
	 * @param {string} paymentOption - The payment option type (e.g., single payment or installment).
	 * @param {number} [paymentsLeft=12] - The number of payments left.
	 * @returns {number} The calculated payment amount.
	 */
	calculatePayment: (premiumTotal, paymentOption, paymentsLeft = 12) => {
		// if it's a single payment, or there is only one payment left, then just send back the total
		if (paymentOption === PAYMENT_TYPE.SINGLE || paymentsLeft <= 1) {
			return premiumTotal;
		}
		// convert dollars to cents
		const premiumCents = Numbers.dollarsToCents(premiumTotal);
		// find the remainder of the payment when a payment can't be divided evenly
		const remainder = premiumCents % paymentsLeft;
		// what is the monthly payment if the total price was equal
		const monthlyPayment = (premiumCents - remainder) / paymentsLeft;
		// add back the remainder to the monthly payment
		const payment = Numbers.centsToDollars(monthlyPayment + remainder);
		return payment;
	},
	/**
	 * Gets the count of kids covered under the policy.
	 *
	 * @param {Array} covered - The array of covered persons.
	 * @returns {number} The count of kids covered.
	 */
	getKidCount: (covered) => covered.filter((person) => person.type === COVERED_TYPE.KID).length,
	/**
	 * Checks if the policy is due for renewal within the number of days passed.
	 *
	 * @param {Object} policy - The policy object.
	 * @param {number} numberOfDays - The number of days to check for renewal.
	 * @returns {boolean} True if the policy is due for renewal soon, false otherwise.
	 */
	isRenewalSoon: (policy = {}, numberOfDays = 0) => {
		// return false if policy is null or not active.
		if (!policy || policy.status !== POLICY_STATUSES.ACTIVE) {
			return false;
		}

		const millisecondsAway = numberOfDays * 86400000;
		const diff = policy.endDateMillisecond - Date.now();
		if (diff > 0 && diff < millisecondsAway) {
			return true;
		}
		return false;
	},
	/**
	 * Checks if the policy is within the grace period for renewal.
	 *
	 * @param {Object} policy - The policy object.
	 * @param {number} numberOfGracePeriodDays - The number of days to check for renewal.
	 * @returns {boolean} True if the policy within grace period, false otherwise.
	 */
	isInsideGracePeriod: (policy = {}, numberOfGracePeriodDays = 0) => {
		// return false if policy is null or not an eligible renewal policy.
		if (!policy || ![POLICY_STATUSES.UNBOUND, POLICY_STATUSES.UNBOUND_NEEDS_ACTION].includes(policy.status)) {
			return false;
		}
		const gracePeriod = numberOfGracePeriodDays * 86400000;
		const gracePeriodEnd = policy.endDateMillisecond + gracePeriod;
		const now = Date.now();

		// If we're past the policy end date, but still within the grace period, return true.
		if ((now > policy.endDateMillisecond) && (now < gracePeriodEnd)) {
			return true;
		}
		return false;
	},
	/**
	 * Checks if the offering is an accident policy.
	 *
	 * @param {string} offering - The offering type.
	 * @returns {boolean} True if the offering is an accident policy, false otherwise.
	 */
	isAccidentPolicy: (offering) => [POLICY_TYPE.ACCIDENT, POLICY_TYPE.ACCIDENT_V2].includes(offering),

	/**
	 * Checks if the offering is an cancellation policy.
	 *
	 * @param {string} offering - The offering type.
	 * @returns {boolean} True if the offering is an cancellation policy, false otherwise.
	 */
	isCancellationPolicy: (offering) => [
		POLICY_TYPE.CANCELLATION,
		POLICY_TYPE.ITC,
		POLICY_TYPE.FORGE_TICKET_CANCELLATION,
	].includes(offering),

	/**
	 * Checks if the offering is a Interplanetary Insurance policy.
	 *
	 * @param {string} offering - The offering type.
	 * @returns {boolean} True if the offering is a Interplanetary Insurance policy, false otherwise.
	 */
	isInterplanetaryPolicy: (offering) => [POLICY_TYPE.II_AIRLOCKACCIDENT, POLICY_TYPE.INTERPLANETARYINSURANCE_AUTO].includes(offering),

	/**
	 * Checks if the offering is a GAIG Season Interruption policy.
	 *
	 * @param {string} offering - The offering type.
	 * @returns {boolean} True if the offering is a GAIG Season Interruption policy, false otherwise.
	 */
	isGaigSiPolicy: (offering) => POLICY_TYPE.GAIG_SI === offering,
	/**
	 * Checks if the offering is a POMI policy.
	 *
	 * @param {string} offering - The offering type.
	 * @returns {boolean} True if the offering is a POMI policy, false otherwise.
	 */
	isPomiPolicy: (offering) => [POLICY_TYPE.POMI_ACCIDENT, POLICY_TYPE.POMI_ACCIDENT_V2].includes(offering),

	/**
		 * Checks if the offering is a GAIG SES 'contractcoverage' policy.
		 *
		 * @param {string} offering - The offering type.
		 * @returns {boolean} True if the offering is a GAIG SES policy, false otherwise.
		 */
	isGaigSesPolicy: (offering) => [POLICY_TYPE.GAIG_SES].includes(offering),

	/**
	 * Checks if the offering is a Starr ADD policy.
	 *
	 * @param {string} offering - The offering type.
	 * @returns {boolean} True if the offering is a Starr ADD policy, false otherwise.
	 */
	isStarrAddPolicy: (offering) => [POLICY_TYPE.AAC, POLICY_TYPE.STARR_AD_AND_D].includes(offering),

	/**
	 * Checks if the offering is a Vigilance policy.
	 *
	 * @param {string} offering - The offering type.
	 * @returns {boolean} True if the offering is a Vigilance policy, false otherwise.
	 */
	isVigilancePolicy: (offering) => [POLICY_TYPE.VIGILANCE_V1, POLICY_TYPE.VIGILANCE_V2].includes(offering),
};

export default PolicyHelper;
